/**
 * useAssessmentDataConverter Hook
 * 处理后端树形数据转换为 AssessmentTable 需要的格式
 */

import { useMemo } from "react";
import type {
  TreeNode,
  ConvertedAssessmentData,
  AssessmentItemConfig,
  AssessmentListItem,
} from "../config/types";

// ==================== Hook 导出 ====================

/**
 * 将后端树形数据转换为 AssessmentTable 所需格式
 * 支持多层级结构：STAGE → DIMENSION → ITEM
 *
 * @param backendData - 后端返回的树形数据
 * @returns 转换后的数据数组
 */
export const useAssessmentDataConverter = (backendData?: TreeNode[]) => {
  // 数据转换函数
  const convertTreeToAssessmentItems = useMemo(
    () =>
      (nodes: TreeNode[] = []): ConvertedAssessmentData[] => {
        const items: ConvertedAssessmentData[] = [];

        nodes.forEach((stageNode) => {
          // 只处理 STAGE 类型的节点
          if (stageNode.nodeType !== "STAGE") return;

          stageNode.childList?.forEach((dimensionNode) => {
            // 只处理 DIMENSION 类型的节点
            if (dimensionNode.nodeType !== "DIMENSION") return;

            const ratingMap: Record<string, string> = {};

            // 提取该维度下的所有 ITEM 节点并转换为 AssessmentItemConfig
            const assessmentItems: AssessmentItemConfig[] =
              dimensionNode.childList
                ?.filter((node) => node.nodeType === "ITEM")
                .map((itemNode) => {
                  // 从 levelList 中找到被 checked 的等级
                  const checkedLevel = itemNode.levelList?.find(
                    (level) => level.checked,
                  );

                  // 将 levelList 中的每个等级转换为 list 中的元素
                  const listItems: AssessmentListItem[] = (
                    itemNode.levelList || []
                  ).map((level, index) => ({
                    id: index + 1,
                    description: level.description,
                    level: level.levelName,
                  }));

                  // 保存选中的等级用于后续初始化
                  if (checkedLevel?.levelName) {
                    ratingMap[itemNode.id] = checkedLevel.levelName;
                  }

                  return {
                    key: itemNode.id,
                    label: itemNode.name,
                    name: itemNode.name,
                    // 将等级列表转换为 list 数组，用于 AssessmentTable 渲染
                    list: listItems,
                  };
                }) || [];

            // 只有当该维度有评估项时才添加
            if (assessmentItems.length > 0) {
              items.push({
                stageId: stageNode.id,
                stageName: stageNode.name,
                dimensionId: dimensionNode.id,
                dimensionName: dimensionNode.name,
                assessmentItems,
                ratingMap,
              });
            }
          });
        });

        return items;
      },
    [],
  );

  // 将转换函数保存在 useMemo 中，以确保引用稳定性
  const convertedData = useMemo(
    () => convertTreeToAssessmentItems(backendData),
    [backendData, convertTreeToAssessmentItems],
  );

  return {
    convertedData,
    convertTreeToAssessmentItems,
  };
};

// ==================== 辅助函数 ====================

/**
 * 验证树形数据的有效性
 */
export const isValidTreeData = (data?: any[]): data is TreeNode[] => {
  return Array.isArray(data) && data.length > 0;
};

/**
 * 解析评估数据 JSON 字符串
 */
export const parseEvaluationData = (jsonString: string): TreeNode[] | null => {
  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (error) {
    return null;
  }
};

/**
 * 从评估项中提取评估数据信息
 */
export const extractEvaluationInfo = (
  evaluationDataArray: any[],
  phaseIdStr: string,
) => {
  return evaluationDataArray.map((item, idx) => ({
    序号: idx + 1,
    评估人: item.purchaserName,
    评估人ID: item.purchaserId,
    评估模板ID: item.evaluationTemplateId,
    评估ID: item.id,
    评估状态:
      item.evaluationStatus === 1
        ? "已评估"
        : item.evaluationStatus === 0
          ? "未评估"
          : "未知",
    evaluationData: item.evaluationDataObj,
  }));
};
